import React, { useState, useEffect } from "react";
import { apiPost, apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { useForm } from "react-hook-form";
import useToastContext from "hooks/useToastContext";
import OInputField from "components/reusable/OInputField";
import { useTranslation } from "react-i18next";
import { preventMaxInput } from "utils/validations";
import formValidation from "../../utils/formValidation";
import dayjs from "dayjs";
import ErrorMessage from "components/ErrorMessage";
import helpers from "utils/helpers";

const ShiftAdd = ({ allShifts, handleCategory, locations, preSelectedEmployee = null, preSelectedDate = null, preSelectedDepartment = null }) => {
  const { t } = useTranslation();
  const notification = useToastContext();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true });

  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateErrors, setDateErrors] = useState({ startDate: "", endDate: "" });
  const [isLoading, setIsLoading] = useState(false);

  // Watch form values for conditional disabling
  const today = dayjs().format("YYYY-MM-DD");
  const watchName = watch("name");
  const watchStartTime = watch("startTime");
  const watchEndTime = watch("endTime");
  const watchBreakDuration = watch("breakDuration");
  const watchLocationId = watch("locationId");

  const getShiftTemplates = async () => {
    try {
      const res = await apiGet(apiPath.getShiftTemplates, {});
      setTemplates(res?.data?.results || []);
    } catch (error) {
      console.log("error:", error);
    }
  };

  const getDepartments = async () => {
    try {
      const res = await apiGet("/v1/hr/jobRequests/department/list", {});
      setDepartments(res?.data?.results || []);
    } catch (error) {
      console.log("error fetching departments:", error);
    }
  };

  // Fetch employees based on department and date range
  const getEmployees = async (deptId, startDt, endDt) => {
    try {
      const params = {};
      if (deptId) params.departmentId = deptId;
      if (startDt) params.startDate = dayjs(startDt).format("YYYY-MM-DD");
      if (endDt) params.endDate = dayjs(endDt).format("YYYY-MM-DD");
      
      const res = await apiGet(apiPath.getAvailableEmployeesList, params);
      setEmployees(res?.data?.results || []);
    } catch (error) {
      console.log("error:", error);
    }
  };

  useEffect(() => {
    getShiftTemplates();
    getDepartments();
    getEmployees(); // Initial load without filters
  }, []);

  // When department or dates change, refresh employee list
  useEffect(() => {
    if (selectedDepartment || startDate || endDate) {
      getEmployees(selectedDepartment, startDate, endDate);
    }
  }, [selectedDepartment, startDate, endDate]);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);

    if (templateId) {
      const template = templates.find((t) => t._id === templateId);
      if (template) {
        setTimeout(() => {
          reset({
            name: template.name,
            startTime: template.startTime,
            endTime: template.endTime,
            breakDuration: template.breakDuration,
            locationId: template.locationId?._id || template.locationId,
          });
        }, 0);
      }
    } else {
      setTimeout(() => {
        reset({
          name: "",
          startTime: "",
          endTime: "",
          breakDuration: 60,
          locationId: "",
        });
      }, 0);
    }
  };

  const validateDates = () => {
    let errors = {};
    if (!startDate) {
      errors.startDate = t("PLEASE_SELECT_START_DATE");
    }
    if (!endDate) {
      errors.endDate = t("PLEASE_SELECT_END_DATE");
    }
    if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate))) {
      errors.endDate = t("END_DATE_MUST_BE_AFTER_START");
    }
    setDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (data) => {
    try {
      if (!validateDates()) return;
      if (!selectedTemplate) {
        notification.error(t("PLEASE_SELECT_A_TEMPLATE"));
        return;
      }
      setIsLoading(true);

      const shiftPayload = {
        name: data.name,
        startTime: data.startTime,
        endTime: data.endTime,
        breakDuration: Number(data.breakDuration),
        locationId: data.locationId,
        departmentId: selectedDepartment || undefined,
        requiredEmployees: 1,
        employeeId: selectedEmployee || undefined,
        date: dayjs(startDate).format("YYYY-MM-DD"),
        endDate: dayjs(endDate).format("YYYY-MM-DD"),
        shiftTemplateId: selectedTemplate,
        startDateTime: `${dayjs(startDate).format("YYYY-MM-DD")} ${data.startTime}`,
        endDateTime: `${dayjs(endDate).format("YYYY-MM-DD")} ${data.endTime}`,
      };

      const res = await apiPost(apiPath.createShift, shiftPayload);

      if (res?.data?.success) {
        allShifts();
        handleCategory(false);
        notification.success(res?.data?.message);
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
      console.log("err:", err);
      notification.error(t("SOMETHING_WENT_WRONG"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 1. Select the Department if passed
    if (preSelectedDepartment) {
      setSelectedDepartment(preSelectedDepartment);
    }
    
    // 2. Select the Employee if passed
    if (preSelectedEmployee) {
      setSelectedEmployee(preSelectedEmployee._id);
    }
    
    // 3. Select the Date if passed
    if (preSelectedDate) {
      setStartDate(preSelectedDate.toDate());
      setEndDate(preSelectedDate.toDate());
    }
  }, [preSelectedEmployee, preSelectedDate, preSelectedDepartment]);

  // Check if template is selected to determine if fields should be disabled
  const isTemplateSelected = !!selectedTemplate;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black opacity-40" onClick={() => handleCategory(false)} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden outline-none focus:outline-none pointer-events-none">
        <div className="relative w-full my-6 mx-auto max-w-2xl pointer-events-auto">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="border-0 rounded-xl shadow-xl relative flex flex-col w-full bg-white outline-none">

              {/* Header */}
              <div className="p-4 flex items-center justify-between border-b border-solid border-slate-100 rounded-t">
                <h3 className="font-bold text-lg text-gray-800">{t("CREATE_SHIFT")}</h3>
                <button type="button" onClick={() => handleCategory(false)} className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600">
                  <span className="text-2xl font-light">×</span>
                </button>
              </div>

              {/* Body */}
              <div className="relative p-5 flex-auto max-h-[80vh] overflow-y-auto">
                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
                  {/* Select Template - REQUIRED */}
                  <div className="relative z-0 w-full group">
                    <select
                      className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
                      value={selectedTemplate}
                      onChange={handleTemplateChange}
                    >
                      <option value="">{t("SELECT_TEMPLATE")}</option>
                      {templates?.map((template) => (
                        <option key={template._id} value={template._id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                    <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                      {t("SHIFT_TEMPLATE")}<span className="text-red-500">*</span>
                    </label>
                    {/* Error Message displayed cleanly below the field */}
                    {!selectedTemplate && (
                      <div className="text-xs text-red-500 mt-1 ml-1 font-medium">
                        {t("PLEASE_SELECT_A_TEMPLATE")}
                      </div>
                    )}
                  </div>

                  {/* Shift Name - Auto-filled from template */}
                  <div className="relative z-0 w-full group">
                    {isTemplateSelected ? (
                      <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
                        {watchName || "-"}
                      </div>
                    ) : (
                      <OInputField
                        inputLabel={<>{t("SHIFT_NAME")}</>}
                        wrapperClassName="relative z-0 w-full group"
                        type="text"
                        name="name"
                        onInput={(e) => preventMaxInput(e, 100)}
                        register={register("name", formValidation["taskName"])}
                        errors={errors}
                      />
                    )}
                    {isTemplateSelected && (
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("SHIFT_NAME")}
                      </label>
                    )}
                  </div>

                  {/* Location - Auto-filled from template */}
                  <div className="relative z-0 w-full group">
                    {isTemplateSelected ? (
                      <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
                        {locations?.find(l => l._id === watchLocationId)?.name || "-"}
                      </div>
                    ) : (
                      <div className="relative z-0 w-full group">
                        <select
                          className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
                          {...register("locationId", { required: t("REQUIRED_FIELD") })}
                        >
                          <option value="">{t("SELECT_LOCATION")}</option>
                          {locations?.map((loc) => (
                            <option key={loc._id} value={loc._id}>{loc.name}</option>
                          ))}
                        </select>
                        <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                          {t("LOCATION")}
                        </label>
                        <ErrorMessage message={errors?.locationId?.message} />
                      </div>
                    )}
                    {isTemplateSelected && (
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("LOCATION")}
                      </label>
                    )}
                  </div>

                  {/* Break Duration - Auto-filled from template */}
                  <div className="relative z-0 w-full group">
                    {isTemplateSelected ? (
                      <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
                        {watchBreakDuration || "-"} min
                      </div>
                    ) : (
                      <OInputField
                        inputLabel={<>{t("BREAK_DURATION_MIN")}</>}
                        wrapperClassName="relative z-0 w-full group"
                        type="number"
                        name="breakDuration"
                        register={register("breakDuration", { required: t("REQUIRED_FIELD"), min: { value: 0 } })}
                        errors={errors}
                      />
                    )}
                    {isTemplateSelected && (
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("BREAK_DURATION_MIN")}
                      </label>
                    )}
                  </div>

                      {/* start time */}
                  <div className="relative z-0 w-full group">
                    {isTemplateSelected ? (
                      <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
                        {watchStartTime || "-"}
                      </div>
                    ) : (
                      <OInputField
                        inputLabel={<>{t("START_TIME")}</>}
                        wrapperClassName="relative z-0 w-full group"
                        type="time"
                        name="startTime"
                        register={register("startTime", { required: t("REQUIRED_FIELD") })}
                        errors={errors}
                      />
                    )}
                    {isTemplateSelected && (
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("START_TIME")}
                      </label>
                    )}
                  </div>

                  {/* End Time - Auto-filled from template */}
                  <div className="relative z-0 w-full group">
                    {isTemplateSelected ? (
                      <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
                        {watchEndTime || "-"}
                      </div>
                    ) : (
                      <OInputField
                        inputLabel={<>{t("END_TIME")}</>}
                        wrapperClassName="relative z-0 w-full group"
                        type="time"
                        name="endTime"
                        register={register("endTime", { required: t("REQUIRED_FIELD") })}
                        errors={errors}
                      />
                    )}
                    {isTemplateSelected && (
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("END_TIME")}
                      </label>
                    )}
                  </div>

                  {/* Start Date - REQUIRED */}
                  <div className="relative z-0 w-full group">
                    <input
                      type="date"
                      min={today}
                      className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      value={startDate ? dayjs(startDate).format("YYYY-MM-DD") : ""}
                      onChange={(e) => {
                        if (e.target.value) {
                          setStartDate(dayjs(e.target.value).toDate());
                          setDateErrors({ ...dateErrors, startDate: "" });
                        }
                      }}
                    />
                    <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                      {t("START_DATE")}<span className="text-red-500">*</span>
                    </label>
                    <ErrorMessage message={dateErrors?.startDate} />
                  </div>

                  {/* End Date - REQUIRED */}
                  <div className="relative z-0 w-full group">
                    <input
                      type="date"
                      min={today}
                      className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      value={endDate ? dayjs(endDate).format("YYYY-MM-DD") : ""}
                      onChange={(e) => {
                        if (e.target.value) {
                          setEndDate(dayjs(e.target.value).toDate());
                          setDateErrors({ ...dateErrors, endDate: "" });
                        }
                      }}
                    />
                    <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                      {t("END_DATE")}<span className="text-red-500">*</span>
                    </label>
                    <ErrorMessage message={dateErrors?.endDate} />
                  </div>

                  {/* Department */}
                  <div className="relative z-0 w-full group">
                    <select
                      className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
                      value={selectedDepartment}
                      onChange={(e) => {
                        setSelectedDepartment(e.target.value);
                        setSelectedEmployee(""); // Reset employee when department changes
                      }}
                    >
                      <option value="">{t("SELECT_DEPARTMENT")}</option>
                      {departments?.map((dept) => (
                        <option key={dept._id} value={dept._id}>{dept.name}</option>
                      ))}
                    </select>
                    <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                      {t("DEPARTMENT")}
                    </label>
                  </div>

                  {/* Assign Employee */}
                  <div className="relative z-0 w-full group">
                    <select
                      className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
                      value={selectedEmployee}
                      onChange={(e) => setSelectedEmployee(e.target.value)}
                    >
                      <option value="">{t("SELECT_EMPLOYEE_OPTIONAL")}</option>
                      {employees?.map((emp) => (
                        <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>
                      ))}
                    </select>
                    <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                      {t("ASSIGN_EMPLOYEE")}
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-center gap-4 p-5 border-t border-solid border-slate-100 rounded-b">
                <button type="button" onClick={() => handleCategory(false)} className="min-w-[120px] text-gray-700 bg-gray-200 font-medium px-6 py-2 text-sm rounded-md hover:bg-gray-300">
                  {t("O_BACK")}
                </button>
                {isLoading ? (
                  <div className="min-w-[120px] flex justify-center bg-[#4F46E5] px-6 py-2 rounded-md">
                    <div className="loading-spinner h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <button type="submit" className="min-w-[120px] bg-[#4F46E5] text-white font-medium px-6 py-2 text-sm rounded-md hover:bg-[#4338CA]">
                    {t("O_ADD")}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ShiftAdd;





// import React, { useState, useEffect } from "react";
// import { apiPost, apiGet } from "../../utils/apiFetch";
// import apiPath from "../../utils/pathObj";
// import { useForm } from "react-hook-form";
// import useToastContext from "hooks/useToastContext";
// import OInputField from "components/reusable/OInputField";
// import { useTranslation } from "react-i18next";
// import { preventMaxInput } from "utils/validations";
// import formValidation from "../../utils/formValidation";
// import dayjs from "dayjs";
// import ErrorMessage from "components/ErrorMessage";
// import helpers from "utils/helpers";

// const ShiftAdd = ({ allShifts, handleCategory, locations, preSelectedEmployee = null, preSelectedDate = null, preSelectedDepartment = null }) => {
//   const { t } = useTranslation();
//   const notification = useToastContext();

//   const {
//     register,
//     handleSubmit,
//     reset,
//     watch,
//     setValue,
//     formState: { errors },
//   } = useForm({ mode: "onChange", shouldFocusError: true });

//   const [templates, setTemplates] = useState([]);
//   const [employees, setEmployees] = useState([]);
//   const [departments, setDepartments] = useState([]);
//   const [selectedTemplate, setSelectedTemplate] = useState("");
//   const [selectedEmployee, setSelectedEmployee] = useState("");
//   const [selectedDepartment, setSelectedDepartment] = useState("");
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [dateErrors, setDateErrors] = useState({ startDate: "", endDate: "" });
//   const [isLoading, setIsLoading] = useState(false);

//   // Watch form values for conditional disabling
//   const today = dayjs().format("YYYY-MM-DD");
//   const watchName = watch("name");
//   const watchStartTime = watch("startTime");
//   const watchEndTime = watch("endTime");
//   const watchBreakDuration = watch("breakDuration");
//   const watchLocationId = watch("locationId");

//   const getShiftTemplates = async () => {
//     try {
//       const res = await apiGet(apiPath.getShiftTemplates, {});
//       setTemplates(res?.data?.results || []);
//     } catch (error) {
//       console.log("error:", error);
//     }
//   };

//   const getDepartments = async () => {
//     try {
//       const res = await apiGet("/v1/hr/jobRequests/department/list", {});
//       setDepartments(res?.data?.results || []);
//     } catch (error) {
//       console.log("error fetching departments:", error);
//     }
//   };

//   // Fetch employees based on department and date range
//   const getEmployees = async (deptId, startDt, endDt) => {
//     try {
//       const params = {};
//       if (deptId) params.departmentId = deptId;
//       if (startDt) params.startDate = dayjs(startDt).format("YYYY-MM-DD");
//       if (endDt) params.endDate = dayjs(endDt).format("YYYY-MM-DD");
      
//       const res = await apiGet(apiPath.getAvailableEmployeesList, params);
//       setEmployees(res?.data?.results || []);
//     } catch (error) {
//       console.log("error:", error);
//     }
//   };

//   useEffect(() => {
//     getShiftTemplates();
//     getDepartments();
//     getEmployees(); // Initial load without filters
//   }, []);

//   // When department or dates change, refresh employee list
//   useEffect(() => {
//     if (selectedDepartment || startDate || endDate) {
//       getEmployees(selectedDepartment, startDate, endDate);
//     }
//   }, [selectedDepartment, startDate, endDate]);

//   const handleTemplateChange = (e) => {
//     const templateId = e.target.value;
//     setSelectedTemplate(templateId);

//     if (templateId) {
//       const template = templates.find((t) => t._id === templateId);
//       if (template) {
//         setTimeout(() => {
//           reset({
//             name: template.name,
//             startTime: template.startTime,
//             endTime: template.endTime,
//             breakDuration: template.breakDuration,
//             locationId: template.locationId?._id || template.locationId,
//           });
//         }, 0);
//       }
//     } else {
//       setTimeout(() => {
//         reset({
//           name: "",
//           startTime: "",
//           endTime: "",
//           breakDuration: 60,
//           locationId: "",
//         });
//       }, 0);
//     }
//   };

//   const validateDates = () => {
//     let errors = {};
//     if (!startDate) {
//       errors.startDate = t("PLEASE_SELECT_START_DATE");
//     }
//     if (!endDate) {
//       errors.endDate = t("PLEASE_SELECT_END_DATE");
//     }
//     if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate))) {
//       errors.endDate = t("END_DATE_MUST_BE_AFTER_START");
//     }
//     setDateErrors(errors);
//     return Object.keys(errors).length === 0;
//   };

//   const onSubmit = async (data) => {
//     try {
//       if (!validateDates()) return;
//       if (!selectedTemplate) {
//         notification.error(t("PLEASE_SELECT_A_TEMPLATE"));
//         return;
//       }
//       setIsLoading(true);

//       const shiftPayload = {
//         name: data.name,
//         startTime: data.startTime,
//         endTime: data.endTime,
//         breakDuration: Number(data.breakDuration),
//         locationId: data.locationId,
//         departmentId: selectedDepartment || undefined,
//         requiredEmployees: 1,
//         employeeId: selectedEmployee || undefined,
//         date: dayjs(startDate).format("YYYY-MM-DD"),
//         endDate: dayjs(endDate).format("YYYY-MM-DD"),
//         shiftTemplateId: selectedTemplate,
//         startDateTime: `${dayjs(startDate).format("YYYY-MM-DD")} ${data.startTime}`,
//         endDateTime: `${dayjs(endDate).format("YYYY-MM-DD")} ${data.endTime}`,
//       };

//       const res = await apiPost(apiPath.createShift, shiftPayload);

//       if (res?.data?.success) {
//         allShifts();
//         handleCategory(false);
//         notification.success(res?.data?.message);
//       } else {
//         notification.error(res?.data?.message);
//       }
//     } catch (err) {
//       console.log("err:", err);
//       notification.error(t("SOMETHING_WENT_WRONG"));
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     // 1. Select the Department if passed
//     if (preSelectedDepartment) {
//       setSelectedDepartment(preSelectedDepartment);
//     }
    
//     // 2. Select the Employee if passed
//     if (preSelectedEmployee) {
//       setSelectedEmployee(preSelectedEmployee._id);
//     }
    
//     // 3. Select the Date if passed
//     if (preSelectedDate) {
//       setStartDate(preSelectedDate.toDate());
//       setEndDate(preSelectedDate.toDate());
//     }
//   }, [preSelectedEmployee, preSelectedDate, preSelectedDepartment]);

//   // Check if template is selected to determine if fields should be disabled
//   const isTemplateSelected = !!selectedTemplate;

//   return (
//     <>
//       <div className="fixed inset-0 z-[60] bg-black opacity-40" onClick={() => handleCategory(false)} />
//       <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden outline-none focus:outline-none pointer-events-none">
//         <div className="relative w-full my-6 mx-auto max-w-2xl pointer-events-auto">
//           <form onSubmit={handleSubmit(onSubmit)}>
//             <div className="border-0 rounded-xl shadow-xl relative flex flex-col w-full bg-white outline-none">

//               {/* Header */}
//               <div className="p-4 flex items-center justify-between border-b border-solid border-slate-100 rounded-t">
//                 <h3 className="font-bold text-lg text-gray-800">{t("CREATE_SHIFT")}</h3>
//                 <button type="button" onClick={() => handleCategory(false)} className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600">
//                   <span className="text-2xl font-light">×</span>
//                 </button>
//               </div>

//               {/* Body */}
//               <div className="relative p-5 flex-auto max-h-[80vh] overflow-y-auto">
//                 <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
//                   {/* Select Template - REQUIRED */}
//                   <div className="relative z-0 w-full group">
//                     <select
//                       className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
//                       value={selectedTemplate}
//                       onChange={handleTemplateChange}
//                     >
//                       <option value="">{t("SELECT_TEMPLATE")}</option>
//                       {templates?.map((template) => (
//                         <option key={template._id} value={template._id}>
//                           {template.name}
//                         </option>
//                       ))}
//                     </select>
//                     <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                       {t("SHIFT_TEMPLATE")}<span className="text-red-500">*</span>
//                     </label>
//                     {/* Error Message displayed cleanly below the field */}
//                     {!selectedTemplate && (
//                       <div className="text-xs text-red-500 mt-1 ml-1 font-medium">
//                         {t("PLEASE_SELECT_A_TEMPLATE")}
//                       </div>
//                     )}
//                   </div>

//                   {/* Shift Name - Auto-filled from template */}
//                   <div className="relative z-0 w-full group">
//                     {isTemplateSelected ? (
//                       <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
//                         {watchName || "-"}
//                       </div>
//                     ) : (
//                       <OInputField
//                         inputLabel={<>{t("SHIFT_NAME")}</>}
//                         wrapperClassName="relative z-0 w-full group"
//                         type="text"
//                         name="name"
//                         onInput={(e) => preventMaxInput(e, 100)}
//                         register={register("name", formValidation["taskName"])}
//                         errors={errors}
//                       />
//                     )}
//                     {isTemplateSelected && (
//                       <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                         {t("SHIFT_NAME")}
//                       </label>
//                     )}
//                   </div>

//                   {/* Location - Auto-filled from template */}
//                   <div className="relative z-0 w-full group">
//                     {isTemplateSelected ? (
//                       <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
//                         {locations?.find(l => l._id === watchLocationId)?.name || "-"}
//                       </div>
//                     ) : (
//                       <div className="relative z-0 w-full group">
//                         <select
//                           className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
//                           {...register("locationId", { required: t("REQUIRED_FIELD") })}
//                         >
//                           <option value="">{t("SELECT_LOCATION")}</option>
//                           {locations?.map((loc) => (
//                             <option key={loc._id} value={loc._id}>{loc.name}</option>
//                           ))}
//                         </select>
//                         <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                           {t("LOCATION")}
//                         </label>
//                         <ErrorMessage message={errors?.locationId?.message} />
//                       </div>
//                     )}
//                     {isTemplateSelected && (
//                       <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                         {t("LOCATION")}
//                       </label>
//                     )}
//                   </div>

//                   {/* Break Duration - Auto-filled from template */}
//                   <div className="relative z-0 w-full group">
//                     {isTemplateSelected ? (
//                       <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
//                         {watchBreakDuration || "-"} min
//                       </div>
//                     ) : (
//                       <OInputField
//                         inputLabel={<>{t("BREAK_DURATION_MIN")}</>}
//                         wrapperClassName="relative z-0 w-full group"
//                         type="number"
//                         name="breakDuration"
//                         register={register("breakDuration", { required: t("REQUIRED_FIELD"), min: { value: 0 } })}
//                         errors={errors}
//                       />
//                     )}
//                     {isTemplateSelected && (
//                       <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                         {t("BREAK_DURATION_MIN")}
//                       </label>
//                     )}
//                   </div>

//                   {/* Start Date - REQUIRED */}
//                   <div className="relative z-0 w-full group">
//                     <input
//                       type="date"
//                       min={today}
//                       className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
//                       value={startDate ? dayjs(startDate).format("YYYY-MM-DD") : ""}
//                       onChange={(e) => {
//                         const val = e.target.value;
//                         if (!val) return;
//                         if (dayjs(val).isBefore(dayjs(today), "day")) {
//                           setDateErrors({ ...dateErrors, startDate: t("START_DATE_CANNOT_BE_IN_PAST") });
//                           return;
//                         }
//                         setStartDate(dayjs(val).toDate());
//                         setDateErrors({ ...dateErrors, startDate: "" });
//                       }}
//                     />
//                     <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                       {t("START_DATE")}<span className="text-red-500">*</span>
//                     </label>
//                     <ErrorMessage message={dateErrors?.startDate} />
//                   </div>

//                   {/* End Time - Auto-filled from template */}
//                   <div className="relative z-0 w-full group">
//                     {isTemplateSelected ? (
//                       <div className="block py-4 px-4 w-full text-sm text-gray-900 bg-gray-50 border-2 rounded-lg border-[#DFDFDF] cursor-not-allowed">
//                         {watchEndTime || "-"}
//                       </div>
//                     ) : (
//                       <OInputField
//                         inputLabel={<>{t("END_TIME")}</>}
//                         wrapperClassName="relative z-0 w-full group"
//                         type="time"
//                         name="endTime"
//                         register={register("endTime", { required: t("REQUIRED_FIELD") })}
//                         errors={errors}
//                       />
//                     )}
//                     {isTemplateSelected && (
//                       <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                         {t("END_TIME")}
//                       </label>
//                     )}
//                   </div>

//                   {/* Start Date - REQUIRED */}
//                   <div className="relative z-0 w-full group">
//                     <input
//                       type="date"
//                       className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
//                       value={startDate ? dayjs(startDate).format("YYYY-MM-DD") : ""}
//                       onChange={(e) => {
//                         if (e.target.value) {
//                           setStartDate(dayjs(e.target.value).toDate());
//                           setDateErrors({ ...dateErrors, startDate: "" });
//                         }
//                       }}
//                     />
//                     <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                       {t("START_DATE")}<span className="text-red-500">*</span>
//                     </label>
//                     <ErrorMessage message={dateErrors?.startDate} />
//                   </div>

//                   {/* End Date - REQUIRED */}
//                   <div className="relative z-0 w-full group">
//                     <input
//                       type="date"
//                       min={today}
//                       className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
//                       value={endDate ? dayjs(endDate).format("YYYY-MM-DD") : ""}
//                       onChange={(e) => {
//                         if (e.target.value) {
//                           setEndDate(dayjs(e.target.value).toDate());
//                           setDateErrors({ ...dateErrors, endDate: "" });
//                         }
//                       }}
//                     />
//                     <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                       {t("END_DATE")}<span className="text-red-500">*</span>
//                     </label>
//                     <ErrorMessage message={dateErrors?.endDate} />
//                   </div>

//                   {/* Department */}
//                   <div className="relative z-0 w-full group">
//                     <select
//                       className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
//                       value={selectedDepartment}
//                       onChange={(e) => {
//                         setSelectedDepartment(e.target.value);
//                         setSelectedEmployee(""); // Reset employee when department changes
//                       }}
//                     >
//                       <option value="">{t("SELECT_DEPARTMENT")}</option>
//                       {departments?.map((dept) => (
//                         <option key={dept._id} value={dept._id}>{dept.name}</option>
//                       ))}
//                     </select>
//                     <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                       {t("DEPARTMENT")}
//                     </label>
//                   </div>

//                   {/* Assign Employee */}
//                   <div className="relative z-0 w-full group">
//                     <select
//                       className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
//                       value={selectedEmployee}
//                       onChange={(e) => setSelectedEmployee(e.target.value)}
//                     >
//                       <option value="">{t("SELECT_EMPLOYEE_OPTIONAL")}</option>
//                       {employees?.map((emp) => (
//                         <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>
//                       ))}
//                     </select>
//                     <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
//                       {t("ASSIGN_EMPLOYEE")}
//                     </label>
//                   </div>
//                 </div>
//               </div>

//               {/* Footer */}
//               <div className="flex items-center justify-center gap-4 p-5 border-t border-solid border-slate-100 rounded-b">
//                 <button type="button" onClick={() => handleCategory(false)} className="min-w-[120px] text-gray-700 bg-gray-200 font-medium px-6 py-2 text-sm rounded-md hover:bg-gray-300">
//                   {t("O_BACK")}
//                 </button>
//                 {isLoading ? (
//                   <div className="min-w-[120px] flex justify-center bg-[#4F46E5] px-6 py-2 rounded-md">
//                     <div className="loading-spinner h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
//                   </div>
//                 ) : (
//                   <button type="submit" className="min-w-[120px] bg-[#4F46E5] text-white font-medium px-6 py-2 text-sm rounded-md hover:bg-[#4338CA]">
//                     {t("O_ADD")}
//                   </button>
//                 )}
//               </div>
//             </div>
//           </form>
//         </div>
//       </div>
//     </>
//   );
// };

// export default ShiftAdd;